import "twin.macro";

import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { Add, Remove } from "@mui/icons-material";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import { useQueryClient } from "@tanstack/react-query";

import { LoadingButton } from "@features/ui";
import { Distributor } from "@models/Distributor";
import { Organization } from "@models/Organization";
import { useApiResource } from "@services/api";
import { ControlledAutocompleteInput, ControlledTextInput } from "@utils/forms";
import useRoleIs from "@utils/useRoleIs";

import {
  distributorsKeyFactory,
  useCreateDistributorMutation,
  useCreateDistributorOrganizationMutation,
  useDeleteDistributorOrganizationMutation,
  useUpdateDistributorMutation,
} from "./data";

type FormData = {
  name: string;
  organizationIds: string[];
};

type DistributorModalProps = {
  onClose: () => void;
  distributor: Distributor | null; // null for create mode
};

const DistributorModal = ({ onClose, distributor }: DistributorModalProps) => {
  const navigate = useNavigate();
  const roleIs = useRoleIs();

  const queryClient = useQueryClient();
  const createDistributorMutation = useCreateDistributorMutation();
  const updateDistributorMutation = useUpdateDistributorMutation();
  const createDistributorOrganizationMutation =
    useCreateDistributorOrganizationMutation();
  const deleteDistributorOrganizationMutation =
    useDeleteDistributorOrganizationMutation();

  const { id: orgId } = useSelector(
    (state: any) => state.currentUser.organization
  );

  const isEditMode = !!distributor;

  // Current organization IDs from the distributor (for edit mode)
  const currentOrgIds = distributor
    ? distributor.distributorOrganizations.map((org) => org.organization.id)
    : [];

  const { data: organizations = [] } = useApiResource<Organization>(
    "organizations",
    {
      params: {
        filter: {
          usesDistributors: true,
          isDistributor: false,
        },
      },
    }
  );

  const {
    control,
    watch,
    formState: { isDirty, dirtyFields },
    handleSubmit,
  } = useForm<FormData>({
    defaultValues: {
      name: distributor?.name || "",
      organizationIds: isEditMode ? currentOrgIds : [orgId],
    },
  });

  const selectedOrgIds = watch("organizationIds");

  // Calculate changes for edit mode
  const addedOrgIds = selectedOrgIds.filter(
    (id) => !currentOrgIds.includes(id)
  );
  const removedOrgIds = currentOrgIds.filter(
    (id) => !selectedOrgIds.includes(id)
  );
  const hasChanges = addedOrgIds.length > 0 || removedOrgIds.length > 0;

  const onSubmit = async (data: FormData) => {
    try {
      let dist = distributor;
      if (!dist) {
        dist = await createDistributorMutation.mutateAsync({
          name: data.name,
        });
      } else if (dirtyFields.name) {
        await updateDistributorMutation.mutateAsync({
          id: dist.id,
          name: data.name,
          ordererIds: dist.orderers.map((o) => o.id),
        });
      }

      await Promise.all([
        ...addedOrgIds.map((orgId) =>
          createDistributorOrganizationMutation.mutateAsync({
            distributorId: dist.id,
            organizationId: orgId,
            brandIds: organizations
              .find((o) => o.id === orgId)
              ?.brands.map((b) => b.id),
          })
        ),
        ...removedOrgIds.map((orgId) =>
          deleteDistributorOrganizationMutation.mutateAsync({
            distributorId: dist.id,
            id: dist.distributorOrganizations.find(
              (o) => o.organization.id === orgId
            )!.id,
          })
        ),
      ]);

      if (!isEditMode) {
        navigate(`/admin/distributors/${dist.id}`);
      }
      onClose();
    } catch (_err) {
      // Invalidate detail and paginated queries
      queryClient.invalidateQueries({
        queryKey: distributorsKeyFactory._def,
      });
    }
  };

  const isLoading =
    createDistributorMutation.isPending || updateDistributorMutation.isPending;

  const error =
    createDistributorMutation.error || updateDistributorMutation.error;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEditMode ? "Manage Distributor" : "Create Distributor"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent tw="pt-0 space-y-4">
          <ControlledTextInput
            name="name"
            control={control}
            label="Distributor Name"
            rules={{ required: "Distributor name is required" }}
          />

          {roleIs("super") && (
            <ControlledAutocompleteInput
              name="organizationIds"
              control={control}
              label="Organizations"
              rules={{ required: "At least one organization is required" }}
              multiple
              options={organizations}
              getSubmitValue={(value) =>
                value.map((v) => (typeof v === "string" ? v : v.id))
              }
            />
          )}

          {/* Change summary for edit mode */}
          {isEditMode && hasChanges && (
            <div>
              {addedOrgIds.length > 0 && (
                <div tw="flex items-center gap-1">
                  <Add tw="text-green-600" fontSize="small" />
                  <span>
                    Adding {addedOrgIds.length} organization
                    {addedOrgIds.length > 1 ? "s" : ""}
                  </span>
                </div>
              )}
              {removedOrgIds.length > 0 && (
                <div tw="flex items-center gap-1">
                  <Remove tw="text-red-600" fontSize="small" />
                  <span>
                    Removing {removedOrgIds.length} organization
                    {removedOrgIds.length > 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </div>
          )}

          {error && (
            <Alert
              severity="error"
              onClose={() => {
                createDistributorMutation.reset();
                updateDistributorMutation.reset();
              }}
            >
              {error.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <LoadingButton
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            variant="contained"
            loading={isLoading}
            disabled={isEditMode && !isDirty}
            type="submit"
          >
            {isEditMode ? "Save Changes" : "Create Distributor"}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DistributorModal;
